import React, { useState, useEffect } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { WithContext as ReactTags } from "react-tag-input";
import { FiImage } from "react-icons/fi";
import { MediaSelectorModal } from "../../../../shared/components/modals";
import { BlogService } from "../../../../services/blogService";
import { BlogCategoryDto } from "../../../../types/app/blogTypes";

interface BlogPost {
  id?: string;
  slug?: string;
  title?: string;
  excerpt?: string;
  content?: string;
  featuredImage?: string;
  category?: string; 
  categories?: string[]; 
  tags?: string[];
  author?: {
    name: string;
    avatar: string;
  };
  publishDate?: string;
  readTime?: string;
  metaTitle?: string;
  metaDescription?: string;
  views?: number;
  featured?: boolean;
  status?: "draft" | "published" | "trashed";
}

interface PostEditorProps {
  post: Partial<BlogPost>;
  onSave: (post: Partial<BlogPost>, isDraft: boolean) => void;
  onCancel: () => void;
  isNew: boolean;
}

const PostEditor: React.FC<PostEditorProps> = ({
  post,
  onSave,
  onCancel,
  isNew,
}) => {
  const [title, setTitle] = useState(post.title || "");
  const [slug, setSlug] = useState(post.slug || "");
  const [excerpt, setExcerpt] = useState(post.excerpt || "");
  const [readTime, setReadTime] = useState(
    post.readTime ? post.readTime.replace(" min read", "") : ""
  );
  const [editorState, setEditorState] = useState(() => {
    if (post.content) {
      const contentBlock = htmlToDraft(post.content);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks
        );
        return EditorState.createWithContent(contentState);
      }
    }
    return EditorState.createEmpty();
  });
  const [category, setCategory] = useState(post.category || "");
  const [categories, setCategories] = useState<string[]>(
    post.categories || (post.category ? [post.category] : [])
  );
  const [tags, setTags] = useState(
    post.tags?.map((tag, index) => ({ id: index.toString(), text: tag })) || []
  );
  const [featuredImage, setFeaturedImage] = useState(post.featuredImage || "");
  const [showPermalink, setShowPermalink] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<
    BlogCategoryDto[]
  >([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const data = await BlogService.getBlogCategories(false);
        setAvailableCategories(Array.isArray(data) ? data : []);
      } catch (error) {
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (post && !isNew) {
      setTitle(post.title || "");
      setSlug(post.slug || "");
      setExcerpt(post.excerpt || "");
      setReadTime(post.readTime ? post.readTime.replace(" min read", "") : "");

      const categoryIdValue = (post as any).categoryId || "";
      setCategory(categoryIdValue);
      setCategories(categoryIdValue ? [categoryIdValue] : []);

      setFeaturedImage(post.featuredImage || "");
      setTags(
        post.tags?.map((tag, index) => ({ id: index.toString(), text: tag })) ||
          []
      );

      if (post.content) {
        const contentBlock = htmlToDraft(post.content);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks
          );
          setEditorState(EditorState.createWithContent(contentState));
        }
      }
    }
  }, [post, isNew]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted && isNew && title && !slug) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setSlug(generatedSlug);
    }

    return () => {
      isMounted = false;
    };
  }, [title, isNew, slug]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlug(e.target.value);
  };

  const handleEditorStateChange = (state: any) => {
    setEditorState(state);
  };

  const handleDeleteTag = (i: number) => {
    setTags(tags.filter((tag, index) => index !== i));
  };

  const handleAddTag = (tag: { id: string; text: string }) => {
    setTags([...tags, tag]);
  };

  const handleDragTag = (
    tag: { id: string; text: string },
    currPos: number,
    newPos: number
  ) => {
    const newTags = tags.slice();
    newTags.splice(currPos, 1);
    newTags.splice(newPos, 0, tag);
    setTags(newTags);
  };

  const handleCategoryChange = (categoryValue: string, isChecked: boolean) => {
    if (isChecked) {
      setCategories([categoryValue]);
      setCategory(categoryValue);
    } else {
      setCategories([]);
      setCategory("");
    }
  };

  const handleSave = (isDraft: boolean) => {
    const contentHtml = draftToHtml(
      convertToRaw(editorState.getCurrentContent())
    );

    const updatedPost: Partial<BlogPost> = {
      ...post,
      title,
      slug,
      excerpt,
      content: contentHtml,
      readTime: readTime ? `${readTime} min read` : "0 min read",
      category: category, 
      categories: categories, 
      tags: tags.map((tag) => tag.text),
      featuredImage,
      status: isDraft ? "draft" : "published",
    };

    onSave(updatedPost, isDraft);
  };

  const handleImageUpload = () => {
    setShowImageModal(true);
  };

  const handleImageConfirm = (imageUrl: string) => {
    setFeaturedImage(imageUrl);
  };

  return (
    <div className="post-editor">
      <div className="editor-header">
        <button className="btn-back" onClick={onCancel}>
          ← Back to Posts
        </button>
      </div>

      <div className="editor-container">
        <div className="editor-main">
          <div className="title-section">
            <div className="field-group">
              <label className="field-label">Title</label>
              <input
                type="text"
                className="post-title-input"
                placeholder="Add title"
                value={title}
                onChange={handleTitleChange}
              />
            </div>

            <div className="field-group">
              <label className="field-label">Excerpt</label>
              <textarea
                className="post-excerpt-input"
                placeholder="Add a brief excerpt or summary (optional)"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                rows={2}
              />
            </div>

            <div className="field-group">
              <label className="field-label">Read Time (minutes)</label>
              <input
                type="number"
                className="post-readtime-input"
                placeholder="5"
                value={readTime}
                onChange={(e) => setReadTime(e.target.value)}
                min="0"
              />
            </div>

            <div className="permalink-section">
              <span className="permalink-label">Permalink:</span>
              {!showPermalink ? (
                <span className="permalink-display">
                  <span className="permalink-base">
                    www.mamimarkets.com/blog/
                  </span>
                  <span className="permalink-slug">{slug || "post-slug"}</span>
                  {isNew && (
                    <button
                      className="btn-edit-permalink"
                      onClick={() => setShowPermalink(true)}
                    >
                      Edit
                    </button>
                  )}
                </span>
              ) : (
                <div className="permalink-edit">
                  <span className="permalink-base">
                    www.mamimarkets.com/blog/
                  </span>
                  <input
                    type="text"
                    className="permalink-input"
                    value={slug}
                    onChange={handleSlugChange}
                  />
                  <button
                    className="btn-ok"
                    onClick={() => setShowPermalink(false)}
                  >
                    OK
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="editor-wrapper">
            <Editor
              editorState={editorState}
              onEditorStateChange={handleEditorStateChange}
              wrapperClassName="editor-wrapper-class"
              editorClassName="editor-class"
              toolbarClassName="editor-toolbar"
              toolbar={{
                options: [
                  "inline",
                  "blockType",
                  "fontSize",
                  "list",
                  "textAlign",
                  "link",
                  "image",
                  "history",
                ],
                inline: {
                  options: ["bold", "italic", "underline"],
                },
                blockType: {
                  inDropdown: true,
                  options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6"],
                },
                list: {
                  options: ["unordered", "ordered"],
                },
              }}
            />
          </div>
        </div>

        <div className="editor-sidebar">
          <div className="sidebar-section publish-section">
            <h3>Publish</h3>
            <div className="publish-actions">
              <button
                className="btn-save-draft"
                onClick={() => handleSave(true)}
              >
                Save Draft
              </button>
              <button className="btn-publish" onClick={() => handleSave(false)}>
                {isNew ? "Publish" : "Update"}
              </button>
            </div>
            {!isNew && <button className="btn-trash">Move to Trash</button>}
          </div>

          <div className="sidebar-section">
            <h3>Categories</h3>
            <div className="categories-list">
              {isLoadingCategories ? (
                <div style={{ padding: "10px", color: "#666" }}>
                  Loading categories...
                </div>
              ) : availableCategories.length === 0 ? (
                <div style={{ padding: "10px", color: "#666" }}>
                  No categories available.{" "}
                  <a
                    href="/app/blog/categories"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#007bff", textDecoration: "none" }}
                  >
                    Create one
                  </a>
                </div>
              ) : (
                availableCategories.map((cat) => (
                  <label key={cat.id} className="category-checkbox">
                    <input
                      type="checkbox"
                      value={cat.id}
                      checked={categories.includes(cat.id)}
                      onChange={(e) =>
                        handleCategoryChange(cat.id, e.target.checked)
                      }
                    />
                    {cat.name}
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Featured Image</h3>
            {featuredImage ? (
              <div className="featured-image-preview">
                <img src={featuredImage} alt="Featured" />
                <button
                  className="btn-remove-image"
                  onClick={() => setFeaturedImage("")}
                >
                  Remove
                </button>
              </div>
            ) : (
              <button className="btn-set-image" onClick={handleImageUpload}>
                <FiImage /> Set featured image
              </button>
            )}
          </div>
        </div>
      </div>

      <MediaSelectorModal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        onSelect={handleImageConfirm}
        title="Select Featured Image"
        currentImage={featuredImage}
      />
    </div>
  );
};

export default PostEditor;
